import { useCallback, useEffect, useRef, useState } from 'react'
import { MIN_COLUMN_WIDTH, clampColumnWidth } from '@/lib/tableColumnPrefs'
import type { UseTableColumnsResult } from '@/hooks/useTableColumns'

/**
 * The drag/keyboard behaviour behind a column resize handle, shared by the
 * shadcn `ResizableTableHead` and the raw-`<th>` `ResizableTh`.
 *
 * Split out of the components because the app has tables of both shapes — the
 * shadcn `Table` primitives and hand-rolled `<table>` markup — and neither is
 * worth rewriting into the other just to gain a resize handle. The rules live
 * here once; the two wrappers only differ in which element they render.
 *
 * ## Why the width is drafted locally
 *
 * The dragged width is held in local state and committed to the store once, on
 * pointer-up. Writing every `pointermove` through would mean a JSON serialize
 * plus a localStorage write per frame — the wrong work to do inside a drag — and
 * would push a hundred intermediate widths through the provider for nobody to
 * see. The cell follows the pointer from the draft, so it still looks live.
 *
 * ## Why pointer events and pointer capture
 *
 * Pointer (not mouse) events so a trackpad, touchscreen and stylus all work.
 * `setPointerCapture` so the drag survives the pointer leaving the 4px handle —
 * without it, moving faster than React re-renders drops the drag, which reads as
 * the handle being broken.
 */
export function useColumnResize(columnId: string, columns: UseTableColumnsResult) {
  const { widthOf, setWidth, resetWidth, isPinned } = columns
  const stored = widthOf(columnId)
  const cellRef = useRef<HTMLTableCellElement>(null)
  const [dragging, setDragging] = useState(false)
  const [draft, setDraft] = useState<number | null>(null)
  // Pointer x and the width at grab time, so the delta is measured from where
  // the grab happened rather than from the cell's left edge — which itself moves
  // when an earlier column is resized mid-drag.
  const origin = useRef<{ x: number; width: number } | null>(null)

  /** The width to render: the live draft while dragging, else the stored one. */
  const width = draft ?? stored
  // A width being DRAGGED right now is as much an instruction as a stored one.
  const pinned = draft != null || isPinned(columnId)

  /** Measured width, for a column that has never been resized. */
  const currentWidth = useCallback(
    () => stored ?? cellRef.current?.getBoundingClientRect().width ?? MIN_COLUMN_WIDTH,
    [stored],
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      // Stop the press reaching a sortable header underneath and triggering a
      // sort on what the user meant as a resize.
      e.preventDefault()
      e.stopPropagation()
      const startWidth = currentWidth()
      origin.current = { x: e.clientX, width: startWidth }
      setDraft(clampColumnWidth(startWidth))
      setDragging(true)
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [currentWidth],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging || !origin.current) return
      setDraft(clampColumnWidth(origin.current.width + (e.clientX - origin.current.x)))
    },
    [dragging],
  )

  const endDrag = useCallback(() => {
    if (!dragging) return
    setDragging(false)
    origin.current = null
    // Commit, then drop the draft so the stored value takes over. Clearing
    // first would flash the pre-drag width for a frame.
    setDraft(current => {
      if (current != null) setWidth(columnId, current)
      return null
    })
  }, [dragging, columnId, setWidth])

  // A pointer-only resize is unusable for anyone who cannot drag, so the handle
  // is focusable and takes arrow keys. This is why it is a `separator` with
  // aria value attributes rather than a decorative div.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const STEP = 16
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setWidth(columnId, currentWidth() - STEP)
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        setWidth(columnId, currentWidth() + STEP)
      } else if (e.key === 'Home') {
        e.preventDefault()
        resetWidth(columnId)
      }
    },
    [columnId, currentWidth, setWidth, resetWidth],
  )

  // While dragging, show the resize cursor everywhere and suppress selection —
  // otherwise dragging across the header selects the header labels.
  useEffect(() => {
    if (!dragging) return
    const prevCursor = document.body.style.cursor
    const prevSelect = document.body.style.userSelect
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      document.body.style.cursor = prevCursor
      document.body.style.userSelect = prevSelect
    }
  }, [dragging])

  return {
    cellRef,
    width,
    pinned,
    dragging,
    /** Spread onto the handle element. */
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown,
      onDoubleClick: () => resetWidth(columnId),
    },
  }
}
